import { html, type TemplateResult } from "lit";
// Figma 421:36 (943x173) and 421:49 (338.4x78.2).
import mantraUrl from "./mantra.svg";
import signatureUrl from "./signature.svg";
// Figma 421:54 / 57 / 59 / 60 / 61 / 56 / 58 / 55 — the red-background doodlegrams.
import doodle5 from "./doodle-5.webp";
import doodle18 from "./doodle-18.webp";
import doodle9 from "./doodle-9.webp";
import doodle7 from "./doodle-7.webp";
import doodle11 from "./doodle-11.webp";
import doodleStar from "./doodle-star.webp";
import doodle3 from "./doodle-3.webp";
import doodle15 from "./doodle-15.webp";

import "./footer.scss";

/**
 * A scattered doodlegram. Positions are percentages of the footer box so the
 * scatter holds its composition at any width. `desktop` is relative to the
 * 1728 x 634 band (Figma 421:35, y4543), `mobile` to the 402 x 540 band
 * (Figma 428:883, y5706). Two of the eight only appear on desktop.
 *
 * These do NOT come from the layer boxes. For the doodlegrams specifically, the
 * layer metadata and the way the file actually renders disagree: the metadata x
 * values sit ~3.5-5% (desktop) further right than the artwork is drawn. Everything
 * else in the file — the lockup, the signature, every other section — matches its
 * metadata exactly, so the discrepancy is isolated to these eight layers.
 *
 * Confirmed against a reference screenshot of the intended desktop footer supplied
 * on 2026-07-31: measuring each doodle in it puts them within 0.7% of the values
 * below, versus 3.6% for the metadata. Sizes agree too, including doodle 7 at
 * 60x125 rather than the 121x128 its layer box claims (its artwork is 244x512,
 * aspect 0.48, and it is drawn clipped at the left edge of the band).
 *
 * So: do not "fix" these back to the metadata. If the layer boxes in Figma are ever
 * corrected, re-derive from the file instead.
 */
interface Doodle {
  readonly src: string;
  readonly w: number;
  readonly h: number;
  readonly desktop: { x: number; y: number };
  readonly mobile?: { x: number; y: number; w: number; h: number };
}

const DOODLES: readonly Doodle[] = [
  // 482:103 / 482:323
  {
    src: doodle5,
    w: 102,
    h: 80,
    desktop: { x: 7.92, y: 15.48 },
    mobile: { x: 1.36, y: 3.94, w: 75, h: 59 },
  },
  // 482:106 / 482:326
  {
    src: doodle18,
    w: 65,
    h: 69,
    desktop: { x: 89.74, y: 8.26 },
    mobile: { x: 81.32, y: 2.98, w: 44, h: 44 },
  },
  // 482:108 / 482:328
  {
    src: doodle9,
    w: 71,
    h: 102,
    desktop: { x: 54.92, y: 7.97 },
    mobile: { x: 43.68, y: 10.17, w: 48, h: 66 },
  },
  // 482:109 — desktop only, drawn clipped at the left edge of the band
  { src: doodle7, w: 60, h: 125, desktop: { x: 0, y: 47.16 } },
  // 482:110 — desktop only
  { src: doodle11, w: 70, h: 71, desktop: { x: 92.04, y: 44.02 } },
  // 482:105 / 482:325
  {
    src: doodleStar,
    w: 107,
    h: 108,
    desktop: { x: 11.34, y: 69.87 },
    mobile: { x: 12.16, y: 75.09, w: 73, h: 74 },
  },
  // 482:107 / 482:327
  {
    src: doodle3,
    w: 98,
    h: 114,
    desktop: { x: 39.17, y: 75.43 },
    mobile: { x: 48.21, y: 67.72, w: 72, h: 81 },
  },
  // 482:104 / 482:324
  {
    src: doodle15,
    w: 89,
    h: 93,
    desktop: { x: 85.75, y: 78.3 },
    mobile: { x: 79.34, y: 77.73, w: 57, h: 57 },
  },
];

export interface FooterProps {
  /** Overrides the bundled "Not for nothing" lockup. */
  readonly mantra: string;
  /** Overrides the bundled signature. */
  readonly signature: string;
}

/**
 * Closing gradient band. Figma desktop 421:35 (1728x634); mobile 428:882
 * (402x540).
 */
export function renderFooter({ mantra, signature }: FooterProps): TemplateResult {
  return html`
    <footer class="valvoline-footer">
      <div class="valvoline-footer__scatter" aria-hidden="true">
        ${DOODLES.map((doodle) => renderDoodle(doodle))}
      </div>

      <div class="valvoline-footer__inner">
        <img class="valvoline-footer__mantra" src=${mantra || mantraUrl} alt="Not for nothing" />
        <img
          class="valvoline-footer__signature"
          src=${signature || signatureUrl}
          alt="Palmer Goforth"
        />
      </div>
    </footer>
  `;
}

function renderDoodle(doodle: Doodle): TemplateResult {
  const { desktop, mobile } = doodle;

  // Geometry travels as custom properties so one media query can switch the
  // whole scatter between the two artboards.
  //
  // Sizes are UNITLESS on purpose: the stylesheet multiplies them by --vv-u (one
  // design pixel) to scale with the artboard, and `calc(60px * 1px)` is invalid —
  // it silently drops the declaration and the doodle falls back to its intrinsic
  // 2x asset size. Positions stay percentages, which need no scaling.
  const vars = [
    `--vv-dx:${desktop.x}%`,
    `--vv-dy:${desktop.y}%`,
    `--vv-dw:${doodle.w}`,
    `--vv-dh:${doodle.h}`,
    mobile ? `--vv-mx:${mobile.x}%` : "",
    mobile ? `--vv-my:${mobile.y}%` : "",
    mobile ? `--vv-mw:${mobile.w}` : "",
    mobile ? `--vv-mh:${mobile.h}` : "",
  ]
    .filter(Boolean)
    .join(";");

  return html`
    <img
      class="valvoline-footer__doodle ${mobile ? "" : "valvoline-footer__doodle--desktop-only"}"
      style=${vars}
      src=${doodle.src}
      alt=""
    />
  `;
}
